import {
  createRoute,
  Link,
  redirect,
  useNavigate,
} from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { rootRoute } from './__root'
import { apiClient } from '@/lib/api'
import { WorkspaceHeader } from '@/components/WorkspaceHeader'
import { UpgradeDialog } from '@/components/panels/UpgradeDialog'
import { useEffectiveFeatures } from '@/hooks/useEffectiveFeatures'
import { usePlatform } from '@/hooks/usePlatform'
import {
  useSequencerContainers,
  useDeleteContainer,
  useJam,
  type ContainerListEntry,
} from '@/hooks/useSequencerProjects'
import { useSequencerStore } from '@/stores/sequencerStore'
import { timeAgo } from '@/lib/format'
import { NewProjectDialog } from '@/components/projects/NewProjectDialog'
import { ConfirmDialog } from '@/components/panels/ConfirmDialog'
import { FaShareAlt as ShareIcon, FaTrash as TrashIcon } from 'react-icons/fa'
import { ShareProjectDialog } from '@/components/sequencer/ShareProjectDialog'

/**
 * Tier-1 Projects list page — mirrors `/layouts`. A project is the
 * top-level user-shareable unit; it will grow to contain multiple
 * patterns, pad banks, MIDI-router config, and slicer state. Today
 * this page is read-only over the existing `PATTERN#` rows on the
 * server (the sync-push loop is paused during this refactor — see
 * `useSyncSequencerProjects.ts`) and clicking a project drops the
 * user into the editor at `/sequencer`.
 *
 * The two-tier `/projects` + `/sequencer` URL split is deliberately
 * incremental: we haven't yet re-homed the editor at `/projects/$id`
 * because that move needs the backend schema to split real Projects
 * from Patterns. Step one (this PR) is the list page; step two will
 * add a project-id param to the editor; step three renames the URL.
 */
export const werkshopProjectsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/werkshop/projects',
  beforeLoad: async () => {
    try {
      await apiClient.get('/api/v1/auth/me')
    } catch {
      throw redirect({ to: '/login' })
    }
  },
  component: ProjectsPage,
})

type ProjectCardData = ContainerListEntry

function ProjectsPage() {
  const { data: projects, isLoading } = useSequencerContainers()
  const { data: jam } = useJam()
  const deleteContainer = useDeleteContainer()
  const { features, tier } = useEffectiveFeatures()
  const platform = usePlatform()
  const navigate = useNavigate()
  const selectContainer = useSequencerStore(s => s.selectContainer)
  const deleteContainerLocal = useSequencerStore(s => s.deleteContainer)
  const [upgradeOpen, setUpgradeOpen] = useState(false)
  const [newProjectOpen, setNewProjectOpen] = useState(false)
  const [shareTarget, setShareTarget] = useState<{
    id: string
    name: string
  } | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<{
    id: string
    name: string
  } | null>(null)

  const canSave = features?.sequencerSave === true && tier !== 'free'
  const maxProjects = features?.maxSequencerProjects ?? 1

  const { owned, shared } = useMemo(() => {
    const typed = Array.isArray(projects) ? projects : []
    const recencyKey = (p: ProjectCardData) =>
      new Date(p.updatedAt ?? p.createdAt ?? 0).getTime()
    return {
      owned: typed
        .filter(p => (p.role ?? 'owner') === 'owner')
        .sort((a, b) => recencyKey(b) - recencyKey(a)),
      shared: typed
        .filter(p => p.role && p.role !== 'owner')
        .sort((a, b) => recencyKey(b) - recencyKey(a)),
    }
  }, [projects])

  const currentContainerId = useSequencerStore(s => s.currentContainerId)
  const openProject = (containerId: string) => {
    // Server ids map 1:1 to local container ids after hydrate, so a
    // click on a card can go straight to /projects/$id. Fall back to
    // the current local container if the server id hasn't been
    // hydrated yet (offline or first-load race).
    const target = containerId || currentContainerId
    selectContainer(target)
    navigate({ to: '/werkshop/projects/$id', params: { id: target } })
  }

  const handleCreate = () => {
    if (!canSave) {
      setUpgradeOpen(true)
      return
    }
    setNewProjectOpen(true)
  }

  const handleDelete = (id: string, name: string) => {
    // In-app dialog (Radix) rather than window.confirm — native
    // dialogs can be permanently silenced by Safari's "block further
    // alerts" checkbox, which leaves the delete button looking broken.
    setDeleteTarget({ id, name })
  }

  const commitDelete = () => {
    if (!deleteTarget) return
    const { id } = deleteTarget
    // Drop the container from the local store FIRST so the sync
    // hook can't fire a trailing PUT on a row the server is about
    // to delete. Without this, the PUT raced the DELETE, 404'd,
    // and the 404 handler re-POSTed the container as a fresh dup —
    // turning a single delete into a resurrection that climbed back
    // to the project cap on every refresh.
    deleteContainerLocal(id)
    deleteContainer.mutate(id)
    setDeleteTarget(null)
  }

  return (
    <div className="min-h-[100dvh] bg-surface-base">
      <WorkspaceHeader
        title="Projects"
        workspaceSwitcher="werkshop"
        rightExtras={
          <button
            onClick={handleCreate}
            className="text-xs text-content-secondary hover:text-content-primary px-2 py-1 rounded hover:bg-accent-muted"
          >
            + New Project
          </button>
        }
      />

      <main
        className={
          platform.isNative
            ? 'w-full px-3 sm:px-4 py-4'
            : 'max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8'
        }
      >
        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent" />
          </div>
        ) : (
          <div className="space-y-10">
            {canSave && (
              <section>
                <h2 className="text-sm font-semibold uppercase tracking-wider text-content-tertiary mb-3">
                  Jam
                </h2>
                <JamCard
                  jam={jam ?? null}
                  onOpen={() =>
                    navigate({
                      to: '/werkshop/projects/$id',
                      params: { id: 'jam' },
                    })
                  }
                />
              </section>
            )}

            {Array.isArray(projects) && projects.length > 0 ? (
              <>
                <section>
                  <h2 className="text-sm font-semibold uppercase tracking-wider text-content-tertiary mb-3">
                    My projects ({owned.length}
                    {maxProjects !== Infinity && ` / ${maxProjects}`})
                  </h2>
                  {owned.length === 0 ? (
                    <p className="text-sm text-content-tertiary italic">
                      You don&apos;t own any projects yet.
                    </p>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {owned.map(project => (
                        <ProjectCard
                          key={project.id}
                          project={project}
                          onOpen={() => openProject(project.id)}
                          onShare={() =>
                            setShareTarget({
                              id: project.id,
                              name: project.name,
                            })
                          }
                          onDelete={() =>
                            handleDelete(project.id, project.name)
                          }
                          deletePending={deleteContainer.isPending}
                        />
                      ))}
                    </div>
                  )}
                </section>

                {shared.length > 0 && (
                  <section>
                    <h2 className="text-sm font-semibold uppercase tracking-wider text-content-tertiary mb-3">
                      Shared with me ({shared.length})
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {shared.map(project => (
                        <ProjectCard
                          key={project.id}
                          project={project}
                          onOpen={() => openProject(project.id)}
                        />
                      ))}
                    </div>
                  </section>
                )}
              </>
            ) : (
              !canSave && (
                <EmptyState onCreate={handleCreate} canSave={canSave} />
              )
            )}
          </div>
        )}
      </main>

      <NewProjectDialog
        isOpen={newProjectOpen}
        onClose={() => setNewProjectOpen(false)}
        onUpgradeNeeded={() => setUpgradeOpen(true)}
      />
      <ShareProjectDialog
        isOpen={shareTarget !== null}
        onClose={() => setShareTarget(null)}
        projectId={shareTarget?.id ?? null}
        projectName={shareTarget?.name ?? 'Project'}
      />
      <UpgradeDialog
        isOpen={upgradeOpen}
        onClose={() => setUpgradeOpen(false)}
      />
      <ConfirmDialog
        isOpen={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={commitDelete}
        title={
          deleteTarget ? `Delete "${deleteTarget.name}"?` : 'Delete project?'
        }
        description="This removes every pattern in the project and can't be undone."
        confirmLabel="Delete"
        destructive
        pending={deleteContainer.isPending}
      />
    </div>
  )
}

function JamCard({
  jam,
  onOpen,
}: {
  jam: {
    name?: string
    updatedAt?: string
    bankCounts?: Record<'A' | 'B' | 'C' | 'D', number>
  } | null
  onOpen: () => void
}) {
  const patternCount = jam
    ? Object.values(jam.bankCounts ?? {}).reduce((acc, n) => acc + n, 0)
    : 0
  return (
    <div className="max-w-md bg-surface-raised rounded-xl shadow-low overflow-hidden border border-brd-subtle card-interactive group">
      <button
        type="button"
        onClick={onOpen}
        aria-label="Open jam"
        className="block w-full text-left"
      >
        <div className="h-20 bg-gradient-to-br from-second to-accent flex items-center justify-center relative">
          <div className="text-white text-3xl opacity-40">✨</div>
        </div>
        <div className="p-4">
          <div className="flex items-center justify-between gap-3">
            <h3 className="text-base font-semibold text-content-primary truncate">
              {jam ? 'Your jam' : 'Start a jam'}
            </h3>
            <span className="text-[10px] uppercase tracking-wider font-semibold text-content-tertiary border border-brd-subtle rounded px-1.5 py-0.5">
              Free slot
            </span>
          </div>
          <p className="text-xs text-content-tertiary mt-1">
            {jam
              ? `${patternCount} pattern${patternCount === 1 ? '' : 's'} · syncs across your devices · not shareable`
              : "One persistent scratchpad — doesn't consume a project slot."}
          </p>
        </div>
      </button>
    </div>
  )
}

function EmptyState({
  onCreate,
  canSave,
}: {
  onCreate: () => void
  canSave: boolean
}) {
  return (
    <div className="text-center py-16 sm:py-24 max-w-md mx-auto">
      <div className="mx-auto w-20 h-20 rounded-2xl bg-accent-muted flex items-center justify-center mb-6">
        <span className="text-3xl">🥁</span>
      </div>
      <h2 className="text-2xl font-bold text-content-primary mb-3">
        Create your first project
      </h2>
      <p className="text-content-secondary mb-2 leading-relaxed">
        A project is a song — a set of patterns, pads, and routing that plays
        together. Open the sequencer to start building.
      </p>
      <p className="text-xs text-content-tertiary mb-8">
        {canSave
          ? 'Your projects save to the cloud and sync across every device you sign in on.'
          : 'Upgrade to save projects to the cloud and share them with others.'}
      </p>
      <button
        onClick={onCreate}
        className="btn-primary btn-lg rounded-xl font-semibold text-lg shadow-medium"
      >
        Open sequencer
      </button>
    </div>
  )
}

interface ProjectCardProps {
  project: ProjectCardData
  onOpen: () => void
  onShare?: () => void
  onDelete?: () => void
  deletePending?: boolean
}

function ProjectCard({
  project,
  onOpen,
  onShare,
  onDelete,
  deletePending,
}: ProjectCardProps) {
  const role = project.role ?? 'owner'
  const updatedAt = project.updatedAt ?? project.createdAt
  return (
    <div className="bg-surface-raised rounded-xl shadow-low overflow-hidden border border-brd-subtle card-interactive group">
      <button
        type="button"
        onClick={onOpen}
        className="block w-full text-left"
        aria-label={`Open ${project.name}`}
      >
        <div className="h-28 bg-gradient-to-br from-accent to-second flex items-center justify-center relative">
          <div className="text-white text-4xl opacity-30">🥁</div>
          {role !== 'owner' && (
            <div className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-black/40 text-[10px] uppercase tracking-wider font-semibold text-white">
              Shared · {role}
            </div>
          )}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-150 flex items-center justify-center">
            <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-150 text-white text-sm font-medium bg-black/40 px-3 py-1 rounded-full">
              Open
            </span>
          </div>
        </div>
        <div className="p-4">
          <h3 className="text-lg font-semibold text-content-primary mb-1 truncate">
            {project.name}
          </h3>
          {role !== 'owner' && project.ownerEmail && (
            <p className="text-xs text-content-tertiary mb-1">
              Shared by {project.ownerEmail}
            </p>
          )}
          <div className="text-xs text-content-tertiary mb-4 flex items-center gap-3 flex-wrap">
            {project.bankCounts && (
              <span>
                {Object.values(project.bankCounts).reduce(
                  (acc, n) => acc + n,
                  0,
                )}{' '}
                pattern
                {Object.values(project.bankCounts).reduce(
                  (acc, n) => acc + n,
                  0,
                ) === 1
                  ? ''
                  : 's'}{' '}
                across{' '}
                {Object.values(project.bankCounts).filter(n => n > 0).length}{' '}
                bank
                {Object.values(project.bankCounts).filter(n => n > 0).length ===
                1
                  ? ''
                  : 's'}
              </span>
            )}
            {updatedAt && <span className="text-brd-emphasis">·</span>}
            {updatedAt && (
              <span title={new Date(updatedAt).toLocaleString()}>
                {timeAgo(updatedAt)}
              </span>
            )}
          </div>
        </div>
      </button>
      <div className="px-4 pb-4 flex gap-2">
        <button
          type="button"
          onClick={() => onOpen()}
          className="flex-1 btn-primary btn-sm text-center"
        >
          Open
        </button>
        {onShare && (
          <button
            type="button"
            onClick={onShare}
            className="btn-secondary btn-sm"
            title="Share this project with another account"
            aria-label={`Share ${project.name}`}
          >
            <ShareIcon size={12} />
          </button>
        )}
        {onDelete && (
          <button
            type="button"
            onClick={onDelete}
            disabled={deletePending}
            className="btn btn-sm bg-destructive text-white hover:bg-destructive/90 disabled:opacity-50"
            title={`Delete ${project.name}`}
            aria-label={`Delete ${project.name}`}
          >
            <TrashIcon size={12} />
          </button>
        )}
      </div>
    </div>
  )
}
